import { busy, notBusy } from '../busy/actions.js'
import {
  handleAddErrors,
  handleGetErrors,
  handleUpdateErrors,
  handleDeleteErrors
} from '../error/actions.js'

import api from '../api.js'
import store from '../store.js'
import storage from '../storage.js'

export const TICKET_CREATE_ACTION = 'TICKET_CREATE_ACTION'
export const TICKET_SHOW_ADD_DIALOG_ACTION = 'TICKET_SHOW_ADD_DIALOG_ACTION'
export const TICKET_HIDE_ADD_DIALOG_ACTION = 'TICKET_HIDE_ADD_DIALOG_ACTION'
export const TICKET_CHANGE_ORDERING_ACTION = 'TICKET_CHANGE_ORDERING_ACTION'
export const TICKET_GET_OK_ACTION = 'TICKET_GET_OK_ACTION'
export const TICKET_UPDATE_ACTION = 'TICKET_UPDATE_ACTION'
export const TICKET_SHOW_EDIT_DIALOG_ACTION = 'TICKET_SHOW_EDIT_DIALOG_ACTION'
export const TICKET_HIDE_EDIT_DIALOG_ACTION = 'TICKET_HIDE_EDIT_DIALOG_ACTION'
export const TICKET_DELETE_ACTION = 'TICKET_DELETE_ACTION'
export const TICKET_SHOW_DELETE_DIALOG_ACTION = 'TICKET_SHOW_DELETE_DIALOG_ACTION'
export const TICKET_HIDE_DELETE_DIALOG_ACTION = 'TICKET_HIDE_DELETE_DIALOG_ACTION'

export const TICKET_SET_ORDER_ACTION = 'TICKET_SET_ORDER_ACTION'
export const TICKET_SET_LIMIT_ACTION = 'TICKET_SET_LIMIT_ACTION'
export const TICKET_SET_OFFSET_ACTION = 'TICKET_SET_OFFSET_ACTION'
export const TICKET_SET_SEARCH_ACTION = 'TICKET_SET_SEARCH_ACTION'
export const TICKET_RESET_SEARCH_ACTION = 'TICKET_RESET_SEARCH_ACTION'
export const TICKET_SET_FILTER_QUERIES_ACTION = 'TICKET_SET_FILTER_QUERIES_ACTION'

const TAG_FILTER_KEY = 'tags_string'
const ASSIGNED_FILTER_KEY = 'assigned_person_name_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'

function filterTicketsByTagNames (items, selectedTagNames, matchMode = TAG_FILTER_MODE_OR) {
  if (!Array.isArray(selectedTagNames) || selectedTagNames.length === 0) {
    return items
  }

  const selected = new Set(
    selectedTagNames
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const tagNames = (item?.tags || [])
      .map((tag) => tag?.name)
      .filter((name) => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim().toLowerCase())

    if (matchMode === TAG_FILTER_MODE_AND) {
      return Array.from(selected).every((selectedTag) => tagNames.includes(selectedTag))
    }
    return tagNames.some((name) => selected.has(name))
  })
}

function filterTicketsByAssignedNames (items, selectedNames) {
  if (!Array.isArray(selectedNames) || selectedNames.length === 0) {
    return items
  }

  const selected = new Set(
    selectedNames
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const assignedName = item?.person_role && item.person_role.length > 0
      ? item.person_role[0]?.person_name
      : null

    return typeof assignedName === 'string' && selected.has(assignedName.trim().toLowerCase())
  })
}

export function ticketCreateAction (ticket) {
  return {
    type: TICKET_CREATE_ACTION,
    ticket
  }
}

export function ticketUpdateAction (ticket) {
  return {
    type: TICKET_UPDATE_ACTION,
    ticket
  }
}

export function ticketDeleteAction (ticket) {
  return {
    type: TICKET_DELETE_ACTION,
    ticket
  }
}

export function showDeleteTicketDialog (ticket) {
  return {
    type: TICKET_SHOW_DELETE_DIALOG_ACTION,
    ticket
  }
}

export function hideDeleteTicketDialog () {
  return {
    type: TICKET_HIDE_DELETE_DIALOG_ACTION
  }
}

export function setOrder (orderBy, order) {
  return {
    type: TICKET_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

export function setLimit (limit) {
  return {
    type: TICKET_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: TICKET_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: TICKET_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Reset search
 */
export function resetSearch () {
  return {
    type: TICKET_RESET_SEARCH_ACTION
  }
}

/**
 * Set filter queries
 */
export function setFilterQueries (filterQueries) {
  storage.putPageSetting('ticket', { filterQueries })
  return {
    type: TICKET_SET_FILTER_QUERIES_ACTION,
    filterQueries
  }
}

/**
 * CREATE
 */
export function addTicket (ticket) {
  return function (dispatch) {
    dispatch(busy())
    api.addTicket(ticket).then(() => {
      dispatch(notBusy())
    }).catch(handleAddErrors('ticket', dispatch))
  }
}

export function toggleFavorite (ticket) {
  return function (dispatch) {
    const favorite = !ticket.is_favorite
    dispatch(busy())
    api.setTicketFavorite(ticket.id, favorite).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('ticket', dispatch))
  }
}

/**
 * READ
 * Reads query params from state
 * But these are cached between views which might not be what you always want.
 * To get around that, this function supports custom query params.
 */
export function getTickets (params = {}) {
  const state = store.getState()
  const { search, limit, offset, orderBy, order, filterQueries } = state.ticket
  const hasExternalParams = Object.keys(params).length > 0

  let query = []
  if (typeof search === 'string' && search.length > 0) {
    const split = search.split(',').map(s => s.trim()).filter(Boolean)
    if (split.length) {
      query.push({ key: 'name', value: split[0], op: '~' })
    }
  }
  const sort = orderBy ? `${orderBy}.${order}` : null

  const allFilters = Array.isArray(filterQueries) ? filterQueries : []
  const tagFilters = allFilters.filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
  const assignedFilters = allFilters.filter((q) => q.key === ASSIGNED_FILTER_KEY && q.op === TAG_FILTER_OP)
  const nonTagFilters = allFilters.filter((q) => {
    const isTagFilter = q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP
    const isAssignedFilter = q.key === ASSIGNED_FILTER_KEY && q.op === TAG_FILTER_OP
    return !isTagFilter && !isAssignedFilter
  })
  const selectedTagNames = tagFilters.map((q) => q.value)
  const selectedAssignedNames = assignedFilters.map((q) => q.value)
  const selectedTagMatchMode = tagFilters.find((q) => q.matchMode === TAG_FILTER_MODE_AND)
    ? TAG_FILTER_MODE_AND
    : TAG_FILTER_MODE_OR
  query = [...query, ...nonTagFilters]

  if (!hasExternalParams) {
    params = { query, limit, offset, sort }
  }

  return function (dispatch) {
    dispatch(busy())

    if (hasExternalParams) {
      api.getTickets(params).then(result => {
        dispatch(notBusy())
        dispatch(receivedTickets(result.data.value))
      }).catch(handleGetErrors('ticket', dispatch))
      return
    }

    const numericLimit = Number(limit) || 0
    const numericOffset = Number(offset) || 0

    if (selectedTagNames.length > 0 || selectedAssignedNames.length > 0) {
      const requestLimit = Math.max(1000, numericLimit + numericOffset)
      api.getTickets({ query, limit: requestLimit, offset: 0, sort }).then(result => {
        let filteredItems = result.data.value
        filteredItems = filterTicketsByTagNames(filteredItems, selectedTagNames, selectedTagMatchMode)
        filteredItems = filterTicketsByAssignedNames(filteredItems, selectedAssignedNames)
        const pagedItems = filteredItems.slice(numericOffset, numericOffset + numericLimit)

        dispatch(notBusy())
        dispatch(receivedTickets(pagedItems))
      }).catch(handleGetErrors('ticket', dispatch))
      return
    }

    api.getTickets(params).then(result => {
      dispatch(notBusy())
      dispatch(receivedTickets(result.data.value))
    }).catch(handleGetErrors('ticket', dispatch))
  }
}

export function receivedTickets (items) {
  return {
    type: TICKET_GET_OK_ACTION,
    items
  }
}

/**
 * UPDATE
 */
export function updateTicket (ticket) {
  return function (dispatch) {
    dispatch(busy())
    api.updateTicket(ticket).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('ticket', dispatch))
  }
}

/**
 * DELETE
 */
export function deleteTicket (ticket) {
  return function (dispatch) {
    dispatch(busy())
    api.deleteTicket(ticket).then(() => {
      dispatch(notBusy())
    }).catch(handleDeleteErrors('ticket', dispatch))
  }
}
